import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { DEFAULT_SCOPE, NAV_SCOPES, scopeForPath, scopeIsUrlResolvable, scopeLinks } from '../../routes/navScopes.jsx';
import { filterLinksByRole } from '../../utils/rbacUtils.js';
import { superUserNav } from '../../config/accessControl.js';

/**
 * Resolves the focused sidebar scope and its RBAC-filtered links.
 *
 * <p>Order matters: the tree is filtered FIRST, then the scope is accepted. A scope whose subtree
 * filters to nothing (the user holds none of its pages) would otherwise render an empty sidebar, so
 * it falls back to the hub instead. That also means a scope needs no permission flag of its own —
 * its nodes' `pagePath`s are the only gate, and `filterLinksByRole` already prunes emptied parents.
 *
 * @returns {{ id: string, label?: string, root?: string, links: Array }}
 */
export const useNavScope = () => {
    const { pathname } = useLocation();
    const navScope = useSelector(state => state.dashboardContext?.navScope);
    const userRole = useSelector(state => state.dashboardContext?.userRole);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const superUser = useSelector(state => state.userDetails?.superUser) || userRole === 'SUPER_USER';

    // The URL wins when it owns a scope, so refresh and deep links land in the right one.
    //
    // The redux field is the fallback ONLY for a scope no URL can resolve. A scope reachable by URL
    // must never also be remembered, or it follows the user onto pages it does not own: entering
    // Admin Settings from the account popup sets navScope='admin' and nothing clears it, so
    // /app-settings, /profile, /ai-credits, /help and /notifications all kept rendering the Admin
    // Settings sidebar — back row and all — until the user clicked out. Every scope is URL-resolvable
    // today (admin/game/portfolio by root, account by membership), so this is inert; the branch
    // stays so a genuinely unreachable scope would still work.
    const remembered = navScope && !scopeIsUrlResolvable(navScope) ? navScope : null;
    const resolved = scopeForPath(pathname) || remembered || DEFAULT_SCOPE;
    // A Super User has no member hub - Homepage, Components, Games and Portfolio are pages the route guard
    // bounces them from - so where a member would see the hub they see Management.
    const candidate = superUser && resolved === DEFAULT_SCOPE ? 'admin' : resolved;

    return useMemo(() => {
        const def = NAV_SCOPES[candidate] || {};
        // Permissions are cleared to null while they refetch on a context change, which filters every
        // subtree to empty. Don't run the hub fallback in that window or the nav flashes the hub and
        // jumps back once permissions land.
        if (!effectivePermissions) return { id: candidate, ...def, links: [] };

        // A Super User's nav is also cut to the pages they may open (isSuperUserAllowedPath), so Management
        // never offers Organizations or Users only to bounce them.
        const allowed = (scopeId) => {
            const links = filterLinksByRole(scopeLinks(scopeId), userRole, effectivePermissions);
            return superUser ? superUserNav(links) : links;
        };
        const links = allowed(candidate);
        if (links.length) return { id: candidate, ...def, links };

        const fallback = superUser ? 'admin' : DEFAULT_SCOPE;
        return { id: fallback, ...(superUser ? NAV_SCOPES.admin : {}), links: allowed(fallback) };
    }, [candidate, userRole, effectivePermissions, superUser]);
};

export default useNavScope;
